<script setup lang="ts">
import { SolButton } from '@solfacil/girassol'
import { useI18n } from 'vue-i18n'
import SharedCardSession from '~/components/shared/CardSession.vue'
import SummaryModelCard from '~/features/my-wallet/blocks/summary/SummaryModelCard.vue'
import SummarySkeleton from '~/features/my-wallet/blocks/summary/SummarySkeleton.vue'
import MyWalletEmpty from '~/features/my-wallet/components/MyWalletEmpty.vue'
import MyWalletError from '~/features/my-wallet/components/MyWalletError.vue'
import { useSummary } from '~/features/my-wallet/composables/useSummary'
import { RECEIVING_MODEL_TAB } from '~/features/my-wallet/constants/wallet-tabs'

// The same summary card of the "Minha carteira" Resumo tab, so the home shows what the wallet shows.
const emit = defineEmits<{
  'update:hidden': [hidden: boolean]
}>()

const { t } = useI18n()

const { screenState, displayData, handleRefresh, openImpersonateModal } = useSummary()

// The basic profile has no access to the wallet: the home leaves the section out instead of an error.
watch(() => screenState.value === 'forbidden', isForbidden => emit('update:hidden', isForbidden), { immediate: true })
</script>

<template>
  <SummaryModelCard
    v-if="screenState === 'ready' && displayData"
    :summary="displayData"
    :details-tab="RECEIVING_MODEL_TAB"
    standard-title
    @retry="handleRefresh"
  />

  <SummarySkeleton v-else-if="screenState === 'loading'" />

  <SharedCardSession v-else-if="screenState !== 'forbidden'" :title="t('overview.payment_model.title')">
    <template #content>
      <div v-if="screenState === 'not_impersonating'" class="payment-model-info-not-impersonate">
        <div class="payment-model-info-not-impersonate-icon">
          <img
            src="/images/period-payment.svg"
            alt="Period payment"
            class="icon"
          >
        </div>
        <div class="payment-model-info-not-impersonate-content">
          <h4 class="payment-model-info-not-impersonate-title">
            {{ t('overview.payment_model.not_impersonate.title') }}
          </h4>
          <p class="payment-model-info-not-impersonate-description">
            {{ t('overview.payment_model.not_impersonate.description') }}
          </p>
          <SolButton
            id="payment-model-info-view-partner"
            size="medium"
            class="payment-model-info-not-impersonate-button"
            @click="openImpersonateModal"
          >
            {{ t('overview.payment_model.not_impersonate.button') }}
          </SolButton>
        </div>
      </div>
      <MyWalletError v-else-if="screenState === 'error'" id="payment-model-info-error" @retry="handleRefresh" />
      <MyWalletEmpty v-else />
    </template>
  </SharedCardSession>
</template>

<style scoped lang="scss">
.payment-model-info-not-impersonate {
  @apply flex flex-col items-center justify-center gap-4 w-full;
  @apply py-10 px-12;

  &-icon {
    @apply flex items-center justify-center;
    @apply w-24 h-24;

    .icon {
      @apply w-full h-full;
    }
  }

  &-content {
    @apply flex flex-col items-center gap-2 w-full;

    .payment-model-info-not-impersonate-title {
      @apply fonts-heading-h4 text-neutral-low-dark;
    }

    .payment-model-info-not-impersonate-description {
      @apply fonts-body-medium-regular text-neutral-low-light;
    }

    .payment-model-info-not-impersonate-button {
      @apply mt-4;
    }
  }
}
</style>
